'use client';

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { AnimatedSection } from '@/components/ui/animated-section';
import { SectionTitle } from '@/components/ui/insights/section-title';
import { CollectorList } from './collector-list';
import {
  Collector as CollectorOld,
  topCollectorsBySpent,
  notableCollectors as defaultNotableCollectors,
} from '@/lib/mock-data/collectors-data';
import { Collector } from '@/types/collector';
import { User, Trophy, Star, MessageSquare, Mail, Calendar, ArrowRightCircle } from 'lucide-react';
import { Collectors } from './collectors';
import { Tier } from '@/types';

interface RecentActivityItem {
  id: string;
  collector: {
    name: string;
    walletAddress: string;
  };
  action: 'joined' | 'purchased' | 'reached_tier' | 'earned_badge';
  timestamp: string;
  details: string;
}

// Mock data for recent activity
const recentActivity: RecentActivityItem[] = [
  {
    id: '1',
    collector: {
      name: 'Alex Thompson',
      walletAddress: '0x71C7656EC7ab88b098defB751B7401B5f6d8976F',
    },
    action: 'purchased',
    timestamp: '2025-03-03T14:33:21Z',
    details: 'Purchased "Abstract Motion #12" for $4,500',
  },
  {
    id: '2',
    collector: {
      name: 'Emma Peterson',
      walletAddress: '0x4B20993Bc481177ec7E8f571ceCaE8A9e22C02db',
    },
    action: 'earned_badge',
    timestamp: '2025-03-03T10:15:45Z',
    details: 'Earned "Loyal Member" badge',
  },
  {
    id: '3',
    collector: {
      name: 'David Kim',
      walletAddress: '0x617F2E2fD72FD9D5503197092aC168c91465E7f2',
    },
    action: 'reached_tier',
    timestamp: '2025-03-02T18:42:03Z',
    details: 'Reached Silver tier',
  },
  {
    id: '4',
    collector: {
      name: 'Sophia Chen',
      walletAddress: '0x2B5AD5c4795c026514f8317c7a215E218DcCD6cF',
    },
    action: 'purchased',
    timestamp: '2025-03-02T15:11:39Z',
    details: 'Purchased "Digital Landscape #7" for $3,200',
  },
  {
    id: '5',
    collector: {
      name: 'Marcus Reynolds',
      walletAddress: '0x8626f6940E2eb28930eFb4CeF49B2d1F2C9C1199',
    },
    action: 'joined',
    timestamp: '2025-03-01T09:27:14Z',
    details: 'Joined the platform',
  },
  {
    id: '6',
    collector: {
      name: 'Collector 12',
      walletAddress: '0x3a54C7e95a94e7Eb9BE60BE48454533b8f4B0cc2',
    },
    action: 'purchased',
    timestamp: '2025-03-01T08:05:52Z',
    details: 'Purchased "Neon City #3" for $1,800',
  },
  {
    id: '7',
    collector: {
      name: 'Collector 8',
      walletAddress: '0x9A7630B609F25bF3A4Ac218e35DBc8c18e2ad580',
    },
    action: 'earned_badge',
    timestamp: '2025-02-28T22:19:10Z',
    details: 'Earned "Frequent Buyer" badge',
  },
];

// Helper function to format timestamp into relative time
const formatRelativeTime = (timestamp: string) => {
  const date = new Date(timestamp);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return 'just now';
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `${diffInMinutes}m ago`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours}h ago`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) {
    return `${diffInDays}d ago`;
  }

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// Helper to get icon for activity type
const getActivityIcon = (action: RecentActivityItem['action']) => {
  switch (action) {
    case 'joined':
      return <User className="h-3 w-3" />;
    case 'purchased':
      return <ArrowRightCircle className="h-3 w-3" />;
    case 'reached_tier':
      return <Trophy className="h-3 w-3" />;
    case 'earned_badge':
      return <Star className="h-3 w-3" />;
    default:
      return <MessageSquare className="h-3 w-3" />;
  }
};

interface LeaderboardSidebarProps {
  topCollectors?: CollectorOld[];
  notableCollectors?: CollectorOld[];
  collectorData?: {
    topCollectors: Collector[];
    notableCollectors: Collector[];
    mostActiveCollectors: Collector[];
    tiersData: Tier[];
  };
}

export function LeaderboardSidebar({
  collectorData,
}: LeaderboardSidebarProps) {
  const [activeTab, setActiveTab] = useState<'activity' | 'engagement'>('activity');

  return (
    <div className="w-full md:pl-4 pr-2 mt-8 md:mt-0 flex flex-col h-full overflow-x-hidden relative">
      {/* Top Collectors Section */}
      <div className="mb-6">
        <SectionTitle>[ TOP COLLECTORS ]</SectionTitle>
        <div className="mt-1">
          <Collectors
            collectors={collectorData?.topCollectors}
            tiers={collectorData?.tiersData}
            showRank={false}
            showDetails={false}
            removeBorders={true}
            hideIds={true}
            expandable={false}
          />
        </div>
      </div>

      {/* Notable Collectors Section */}
      <div className="mb-6">
        <SectionTitle>[ NOTABLE COLLECTORS ]</SectionTitle>
        <div className="mt-1">
          <Collectors
            collectors={collectorData?.notableCollectors}
            tiers={collectorData?.tiersData}
            showRank={false}
            showDetails={false}
            removeBorders={true}
            hideIds={true}
            expandable={false}
          />
        </div>
      </div>

      {/* Activity Title - fixed position */}
{/*       <div className="sticky top-0 bg-white z-10 pb-2 border-b border-zinc-50">
        <motion.div
          className="pb-2"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="flex items-center justify-between">
            <SectionTitle>[ COLLECTOR ACTIVITY ]</SectionTitle>

            // Tab Selector
            <div className="flex text-[8px] font-mono">
              <button
                className={`px-1.5 py-0.5 border-b ${activeTab === 'activity' ? 'border-black' : 'border-transparent'}`}
                onClick={() => setActiveTab('activity')}
              >
                RECENT
              </button>
              <button
                className={`px-1.5 py-0.5 border-b ${activeTab === 'engagement' ? 'border-black' : 'border-transparent'}`}
                onClick={() => setActiveTab('engagement')}
              >
                ENGAGEMENT
              </button>
            </div>
          </div>
        </motion.div>
      </div> */}

      {/* Activity feed */}
      {/* {activeTab === 'activity' && (
        <AnimatedSection className="grow overflow-y-auto" delay={0.3}>
          <div className="space-y-2 mt-2">
            {recentActivity.map(activity => (
              <div
                key={activity.id}
                className="border-b border-zinc-100 pb-2 last:border-b-0 hover:bg-zinc-50 p-1 rounded-sm transition-colors"
              >
                <div className="flex items-start">
                  // Activity Icon
                  <div className="w-4 h-4 rounded-sm bg-zinc-100 mr-2 shrink-0 flex items-center justify-center text-zinc-700">
                    {getActivityIcon(activity.action)}
                  </div>

                  // Content
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <p className="text-[10px] font-mono truncate">
                        {activity.collector.walletAddress.substring(0, 6)}...
                        {activity.collector.walletAddress.substring(activity.collector.walletAddress.length - 4)}
                      </p>
                      <span className="text-[8px] text-zinc-500 whitespace-nowrap ml-1">
                        {formatRelativeTime(activity.timestamp)}
                      </span>
                    </div>

                    <p className="text-[9px] text-zinc-600 truncate mt-0.5">{activity.details}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </AnimatedSection>
      )} */}

      {/* Engagement Stats */}
      {/* {activeTab === 'engagement' && (
        <AnimatedSection className="grow overflow-y-auto" delay={0.3}>
          <div className="space-y-4 mt-2">
            // Weekly New Collectors
            <div className="border border-zinc-200 rounded-md p-3">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-[9px] font-medium">NEW COLLECTORS</h4>
                <User className="h-3 w-3 text-zinc-500" />
              </div>
              <p className="text-lg font-mono">+12</p>
              <p className="text-[8px] text-zinc-500">Past 7 days</p>
              <div className="mt-2 h-1 bg-zinc-100 rounded-full overflow-hidden">
                <div className="h-full bg-zinc-800 w-[75%]"></div>
              </div>
              <p className="text-[8px] text-zinc-500 mt-1">+20% from previous week</p>
            </div>

            // Engagement Rate
            <div className="border border-zinc-200 rounded-md p-3">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-[9px] font-medium">ENGAGEMENT RATE</h4>
                <MessageSquare className="h-3 w-3 text-zinc-500" />
              </div>
              <p className="text-lg font-mono">68%</p>
              <p className="text-[8px] text-zinc-500">Average collector activity</p>
              <div className="mt-2 h-1 bg-zinc-100 rounded-full overflow-hidden">
                <div className="h-full bg-zinc-800 w-[68%]"></div>
              </div>
              <p className="text-[8px] text-zinc-500 mt-1">+5% from previous month</p>
            </div>

            // Collector Retention
            <div className="border border-zinc-200 rounded-md p-3">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-[9px] font-medium">RETENTION RATE</h4>
                <Calendar className="h-3 w-3 text-zinc-500" />
              </div>
              <p className="text-lg font-mono">92%</p>
              <p className="text-[8px] text-zinc-500">Monthly collector retention</p>
              <div className="mt-2 h-1 bg-zinc-100 rounded-full overflow-hidden">
                <div className="h-full bg-zinc-800 w-[92%]"></div>
              </div>
              <p className="text-[8px] text-zinc-500 mt-1">+2% from previous month</p>
            </div>

            // Email Subscription Rate
            <div className="border border-zinc-200 rounded-md p-3">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-[9px] font-medium">EMAIL OPT-IN</h4>
                <Mail className="h-3 w-3 text-zinc-500" />
              </div>
              <p className="text-lg font-mono">83%</p>
              <p className="text-[8px] text-zinc-500">Collectors subscribed to updates</p>
              <div className="mt-2 h-1 bg-zinc-100 rounded-full overflow-hidden">
                <div className="h-full bg-zinc-800 w-[83%]"></div>
              </div>
              <p className="text-[8px] text-zinc-500 mt-1">Healthy subscription rate</p>
            </div>
          </div>
        </AnimatedSection>
      )} */}
    </div>
  );
}
